import {
  useEffect,
  useState,
  useMemo,
} from 'react';
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import isNil from 'lodash/isNil';
import {
  region,
  subscriptionKey,
} from 'env';
import {
  useMixpanelTrack,
} from 'core/hooks';

export type VoiceRecognitionData = {
  unprocessedText: string;
  text: string;
  loading: boolean;
  listening: boolean;
  startVoiceRecognition: () => void;
  stopVoiceRecognition: () => void;
};

export type SpeechPhraseList = {
  phrases: readonly string[];
  weight?: number;
};

type Props = {
  trackingLabel?: string;
  phraseList?: SpeechPhraseList;
};

const speechConfig = sdk.SpeechConfig.fromSubscription(subscriptionKey, region);
speechConfig.enableDictation();
speechConfig.setServiceProperty('punctuation', 'explicit', sdk.ServicePropertyChannel.UriQueryParameter);
const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();

export const medicalDictationPhraseList: SpeechPhraseList = {
  phrases: [
    'Santyl',
    'Iodosorb',
    'Anasept',
    'Promogran',
    'Debrided',
    'Debride',
    'PVD',
  ],
  weight: 1,
};

function getRecognizer() {
  return new sdk.SpeechRecognizer(speechConfig, audioConfig);
}

function applyPhraseList(
  recognizer: sdk.SpeechRecognizer,
  config: SpeechPhraseList | undefined,
) {
  if (isNil(config)) {
    return;
  }

  const phraseList = sdk.PhraseListGrammar.fromRecognizer(recognizer);
  config.phrases.forEach((phrase) => phraseList.addPhrase(phrase));
  phraseList.setWeight(config.weight ?? 1);
}

export default function useSpeechToText({
  trackingLabel,
  phraseList = medicalDictationPhraseList,
}: Props): VoiceRecognitionData {
  const mixpanelTrack = useMixpanelTrack();
  const recognizer = useMemo(() => {
    const currentRecognizer = getRecognizer();
    applyPhraseList(currentRecognizer, phraseList);
    return currentRecognizer;
  }, [phraseList]);
  const [loading, setLoading] = useState<boolean>(false);
  const [listening, setListening] = useState<boolean>(false);
  const [unprocessedText, setUnprocessedText] = useState<string>('');
  const [text, setText] = useState<string>('');

  useEffect(() => {
    recognizer.recognizing = (s, { result }) => {
      setUnprocessedText(result?.text?.trim());
    };

    recognizer.recognized = (s, { result }) => {
      const { reason, text } = result;
      if (text && reason === sdk.ResultReason.RecognizedSpeech) {
        setText(text);
        setUnprocessedText('');
      } else if (reason === sdk.ResultReason.NoMatch) {
        console.error('Speech could not be recognized.');
      }
    };
  }, [recognizer]);

  const startVoiceRecognition = () => {
    setLoading(true);
    recognizer.startContinuousRecognitionAsync(() => {
      setLoading(false);
      setListening(true);
    }, (err: string) => {
      setLoading(false);
      console.error('Failed to startContinuousRecognitionAsync', err);
    });
    mixpanelTrack('startedDictation', { trackingLabel });
  };

  const stopVoiceRecognition = () => {
    setLoading(true);
    recognizer.stopContinuousRecognitionAsync(() => {
      setLoading(false);
      setListening(false);
      setText('');
      setUnprocessedText('');
    }, (err: string) => {
      setLoading(false);
      console.error('Failed to stopContinuousRecognitionAsync', err);
    });
    mixpanelTrack('stoppedDictation', { trackingLabel });
  };

  return {
    unprocessedText,
    text,
    loading,
    listening,
    startVoiceRecognition,
    stopVoiceRecognition,
  };
}
